import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authManager } from '../../services/authManager';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredCapabilities?: string[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiredCapabilities,
  redirectTo = '/workspace'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasRoleAccess, setHasRoleAccess] = useState(true);
  const [hasCapabilityAccess, setHasCapabilityAccess] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🚀 ProtectedRoute - checkAuth démarré pour:', location.pathname);
      
      try {
        // Vérifier si l'utilisateur est authentifié
        const authenticated = authManager.isAuthenticated();
        console.log('🔐 ProtectedRoute - Authentifié?', authenticated);
        
        if (authenticated) {
          // Vérifier que le token est toujours valide
          const token = authManager.getAccessToken();
          console.log('🎫 ProtectedRoute - Token présent?', !!token);
          
          if (token) {
            // Le gestionnaire d'authentification gère automatiquement la validation
            setIsAuthenticated(true);
            
            // Si l'utilisateur est admin, il a accès à tout
            const user = authManager.getUser();
            console.log('👤 ProtectedRoute - User object:', {
              email: user?.email,
              role: user?.role,
              roles: user?.roles,
              capabilities: user?.capabilities,
              fullUser: user
            });
            
            const userRole = user?.role || '';
            const userRoles = user?.roles || [];
            const normalizedRole = String(userRole || '').toLowerCase().trim();
            const normalizedRoles = userRoles.map((r: string) => String(r || '').toLowerCase().trim());
            
            console.log('📝 ProtectedRoute - Normalisation des rôles:', {
              userRole,
              userRoles,
              normalizedRole,
              normalizedRoles
            });
            
            // Vérifier si admin de plusieurs façons pour être sûr
            const hasRoleAdmin1 = normalizedRole === 'admin';
            const hasRoleAdmin2 = normalizedRoles.includes('admin');
            const hasRoleAdmin3 = authManager.hasRole('admin');
            const isAdmin = hasRoleAdmin1 || hasRoleAdmin2 || hasRoleAdmin3;
            
            console.log('👑 ProtectedRoute - Vérification Admin:', {
              'normalizedRole === "admin"': hasRoleAdmin1,
              'normalizedRoles.includes("admin")': hasRoleAdmin2,
              'authManager.hasRole("admin")': hasRoleAdmin3,
              'isAdmin (final)': isAdmin
            });
            
            console.log('🔑 ProtectedRoute - Paramètres de route:', {
              allowedRoles,
              requiredCapabilities,
              redirectTo
            });
            
            const roleAllowed = !allowedRoles || allowedRoles.length === 0
              ? true
              : isAdmin || authManager.hasAnyRole(allowedRoles);
            const capabilityAllowed = !requiredCapabilities || requiredCapabilities.length === 0
              ? true
              : isAdmin || authManager.hasAllCapabilities(requiredCapabilities);
            
            console.log('✅ ProtectedRoute - Résultats de vérification:', {
              roleAllowed,
              capabilityAllowed,
              'authManager.hasAnyRole(allowedRoles)': allowedRoles && allowedRoles.length > 0 ? authManager.hasAnyRole(allowedRoles) : 'N/A',
              'authManager.hasAllCapabilities(requiredCapabilities)': requiredCapabilities && requiredCapabilities.length > 0 ? authManager.hasAllCapabilities(requiredCapabilities) : 'N/A',
            });
            
            // Debug TOUJOURS activé pour les routes problématiques (settings, projects, integrations)
            if (location.pathname.includes('settings') || location.pathname.includes('projects') || location.pathname.includes('integrations')) {
              console.log(`🔍 ProtectedRoute - Debug DÉTAILLÉ ${location.pathname}:`, {
                path: location.pathname,
                email: user?.email,
                role: user?.role,
                roles: user?.roles,
                normalizedRole,
                normalizedRoles,
                getRoles: authManager.getRoles(),
                isAdmin,
                hasRoleAdmin: authManager.hasRole('admin'),
                requiredCapabilities,
                userCapabilities: user?.capabilities,
                hasAllCapabilities: authManager.hasAllCapabilities(requiredCapabilities || []),
                roleAllowed,
                capabilityAllowed,
                'authManager.getUser()': authManager.getUser(),
              });
            }

            setHasRoleAccess(roleAllowed);
            setHasCapabilityAccess(capabilityAllowed);
            
            console.log('🎯 ProtectedRoute - États définis:', {
              hasRoleAccess: roleAllowed,
              hasCapabilityAccess: capabilityAllowed
            });
            
            // Log si l'accès est refusé
            if (!roleAllowed || !capabilityAllowed) {
              console.warn('⚠️ ProtectedRoute - ACCÈS REFUSÉ:', {
                path: location.pathname,
                email: user?.email,
                roleAllowed,
                capabilityAllowed,
                isAdmin,
                'Raison roleAllowed': !roleAllowed ? 'Échec vérification rôle' : 'OK',
                'Raison capabilityAllowed': !capabilityAllowed ? 'Échec vérification capacité' : 'OK',
              });
            } else {
              console.log('✅ ProtectedRoute - ACCÈS AUTORISÉ pour:', location.pathname);
            }
          } else {
            // Token manquant, rediriger vers la connexion
            console.warn('⚠️ ProtectedRoute - Token manquant');
            setIsAuthenticated(false);
            setHasRoleAccess(false);
            setHasCapabilityAccess(false);
          }
        } else {
          console.warn('⚠️ ProtectedRoute - Utilisateur non authentifié');
          setIsAuthenticated(false);
          setHasRoleAccess(false);
          setHasCapabilityAccess(false);
        }
      } catch (error) {
        console.error('❌ ProtectedRoute - Erreur lors de la vérification d\'authentification:', error);
        setIsAuthenticated(false);
        setHasRoleAccess(false);
        setHasCapabilityAccess(false);
      } finally {
        setIsLoading(false);
        console.log('🏁 ProtectedRoute - checkAuth terminé');
      }
    };

    checkAuth();
  }, [location.pathname, allowedRoles, requiredCapabilities]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🚫 ProtectedRoute - Redirection vers /login (non authentifié)');
    // Rediriger vers la page de connexion avec l'URL de retour
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRoleAccess) {
    console.log(`🚫 ProtectedRoute - Redirection vers ${redirectTo} (accès rôle refusé)`);
    return <Navigate to={redirectTo} replace />;
  }

  if (!hasCapabilityAccess) {
    console.log(`🚫 ProtectedRoute - Redirection vers ${redirectTo} (accès capacité refusé)`);
    return <Navigate to={redirectTo} replace />;
  }

  console.log('✅ ProtectedRoute - Rendu des enfants pour:', location.pathname);
  return <>{children}</>;
};

export default ProtectedRoute;
